/**
 * TipTap-based composer input for the AI Chat surface.
 *
 * Replaces the plain `<textarea>` so `/`-picked Corpus items can render as
 * inline atom chips (with a small icon, title, and inline × control) instead
 * of raw `@Label` text. Chips are inline atoms — the caret treats each as
 * a single character and backspace deletes the whole chip.
 *
 * The editor exposes an imperative handle so ChatPanel can insert chips
 * from the `/` picker, read the plain text + structured segments at submit
 * time, and clear/focus the surface between turns.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { EditorContent, ReactNodeViewRenderer, useEditor } from "@tiptap/react";
import { Node, mergeAttributes } from "@tiptap/core";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import HardBreak from "@tiptap/extension-hard-break";
import Placeholder from "@tiptap/extension-placeholder";
import { CorpusChipPill, type CorpusChipData, type CorpusChipKind } from "./CorpusChipPill";

export type ChatSegment =
  | { type: "text"; text: string }
  | { type: "chip"; kind: CorpusChipKind; id: string; label: string };

export type ChatComposerEditorHandle = {
  focus: () => void;
  clear: () => void;
  isEmpty: () => boolean;
  getPlainText: () => string;
  getSegments: () => ChatSegment[];
  setPlainText: (text: string) => void;
  insertChip: (chip: CorpusChipData) => void;
};

type Props = {
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
  heightPx?: number;
  onSubmit?: () => void;
  onSlashTrigger?: () => void;
  onChange?: (isEmpty: boolean) => void;
  className?: string;
};

// ---- Custom inline atom node ----

const ChipNodeView = ({ node, deleteNode, editor }: NodeViewProps) => {
  const kind = (node.attrs.kind as CorpusChipKind) ?? "doc";
  const label = (node.attrs.label as string) ?? "";
  const canEdit = editor?.isEditable ?? true;
  return (
    <NodeViewWrapper as="span" className="corpus-chip-nvw inline-block align-baseline">
      <CorpusChipPill
        kind={kind}
        label={label}
        onRemove={canEdit ? () => deleteNode() : undefined}
      />
    </NodeViewWrapper>
  );
};

const CorpusChipNode = Node.create({
  name: "corpusChip",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      kind: { default: "doc" },
      chipId: { default: "" },
      label: { default: "" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-corpus-chip]",
        getAttrs: (el) => {
          if (!(el instanceof HTMLElement)) return false;
          return {
            kind: el.getAttribute("data-kind") || "doc",
            chipId: el.getAttribute("data-chip-id") || "",
            label: el.getAttribute("data-label") || el.textContent || "",
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-corpus-chip": "true",
        "data-kind": node.attrs.kind,
        "data-chip-id": node.attrs.chipId,
        "data-label": node.attrs.label,
      }),
      `@${node.attrs.label}`,
    ];
  },

  renderText({ node }) {
    return `@${node.attrs.label}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(ChipNodeView);
  },
});

export const ChatComposerEditor = forwardRef<ChatComposerEditorHandle, Props>(
  (
    {
      placeholder,
      disabled,
      minHeight = 88,
      heightPx,
      onSubmit,
      onSlashTrigger,
      onChange,
      className,
    },
    ref,
  ) => {
    const onSubmitRef = useRef(onSubmit);
    const onSlashRef = useRef(onSlashTrigger);
    const onChangeRef = useRef(onChange);
    useEffect(() => {
      onSubmitRef.current = onSubmit;
    }, [onSubmit]);
    useEffect(() => {
      onSlashRef.current = onSlashTrigger;
    }, [onSlashTrigger]);
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    const extensions = useMemo(
      () => [
        Document,
        Paragraph,
        Text,
        HardBreak,
        CorpusChipNode,
        Placeholder.configure({
          placeholder: placeholder ?? "Ask…",
          showOnlyWhenEditable: true,
        }),
      ],
      [placeholder],
    );

    const editor = useEditor({
      extensions,
      editable: !disabled,
      content: "",
      editorProps: {
        attributes: {
          class:
            "chat-composer-editor w-full min-h-0 h-full overflow-auto px-3 py-2 text-sm leading-6 focus:outline-none",
          "data-testid": "chat-composer-editor",
        },
        handleKeyDown: (view, event) => {
          // Enter (no shift/alt/ctrl) → submit
          if (event.key === "Enter" && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
            if (event.isComposing) return false;
            event.preventDefault();
            event.stopPropagation();
            onSubmitRef.current?.();
            return true;
          }
          // `/` at start-of-line or after whitespace → open picker
          if (event.key === "/") {
            const { state } = view;
            const { $from } = state.selection;
            const before = $from.nodeBefore;
            const isStart = $from.parentOffset === 0;
            const prevChar = before && before.isText ? (before.text ?? "").slice(-1) : "";
            const atBoundary = isStart || /\s/.test(prevChar);
            if (atBoundary && onSlashRef.current) {
              event.preventDefault();
              event.stopPropagation();
              onSlashRef.current();
              return true;
            }
          }
          return false;
        },
      },
      onUpdate: ({ editor }) => {
        onChangeRef.current?.(editor.isEmpty);
      },
    });

    useEffect(() => {
      if (!editor) return;
      editor.setEditable(!disabled);
    }, [editor, disabled]);

    const insertChip = useCallback(
      (chip: CorpusChipData) => {
        if (!editor) return;
        editor
          .chain()
          .focus()
          .insertContent({
            type: "corpusChip",
            attrs: { kind: chip.kind, chipId: chip.id, label: chip.label },
          })
          .run();
      },
      [editor],
    );

    const getPlainText = useCallback((): string => {
      if (!editor) return "";
      // TipTap's getText inserts a blockSeparator between paragraphs; we want
      // real newlines, and each `corpusChip` should serialize via renderText
      // to `@Label`.
      return editor.getText({ blockSeparator: "\n" }).trim();
    }, [editor]);

    const getSegments = useCallback((): ChatSegment[] => {
      if (!editor) return [];
      const segments: ChatSegment[] = [];
      let textBuf = "";
      const flush = () => {
        if (textBuf.length > 0) {
          segments.push({ type: "text", text: textBuf });
          textBuf = "";
        }
      };
      editor.state.doc.descendants((node, _pos, parent) => {
        if (node.type.name === "corpusChip") {
          flush();
          segments.push({
            type: "chip",
            kind: (node.attrs.kind as CorpusChipKind) ?? "doc",
            id: (node.attrs.chipId as string) ?? "",
            label: (node.attrs.label as string) ?? "",
          });
          return false;
        }
        if (node.isText) {
          textBuf += node.text ?? "";
          return false;
        }
        if (node.type.name === "hardBreak") {
          textBuf += "\n";
          return false;
        }
        if (node.type.name === "paragraph") {
          if (parent && (parent.childCount ?? 0) > 1 && textBuf.length > 0) {
            // paragraph boundary: newline between paragraphs
            textBuf += "\n";
          }
          return true;
        }
        return true;
      });
      flush();
      return segments;
    }, [editor]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editor?.chain().focus().run(),
        clear: () => editor?.chain().clearContent(true).run(),
        isEmpty: () => (editor ? editor.isEmpty : true),
        getPlainText,
        getSegments,
        setPlainText: (text: string) => {
          if (!editor) return;
          editor.chain().setContent({
            type: "doc",
            content: [
              {
                type: "paragraph",
                content: text ? [{ type: "text", text }] : [],
              },
            ],
          }, true).focus("end").run();
        },
        insertChip,
      }),
      [editor, getPlainText, getSegments, insertChip],
    );

    const style: React.CSSProperties = heightPx
      ? { height: `${heightPx}px`, minHeight: `${minHeight}px` }
      : { minHeight: `${minHeight}px` };

    return (
      <div
        className={
          "chat-composer-editor-wrap w-full overflow-hidden rounded-md border border-input bg-background focus-within:ring-1 focus-within:ring-ring " +
          (disabled ? "opacity-60 pointer-events-none " : "") +
          (className ?? "")
        }
        style={style}
      >
        <EditorContent editor={editor} className="h-full" />
      </div>
    );
  },
);
ChatComposerEditor.displayName = "ChatComposerEditor";
